import { useClickOutside } from "../../hooks/ClickOutsideHook";
import { KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { IDropdownButtonItem, IDropdownButtonProps } from "../../interfaces/ButtonDropdowns";
import { handleKeyDown } from "../../utils";
import { ChevronDownIcon, ChevronUpIcon, XIcon } from "lucide-react";

export const GaDropdownButton = (props: IDropdownButtonProps) => {
    const [ref, showList, setShowList] = useClickOutside<HTMLDivElement>();
    const [focusedIndex, setFocusedIndex] = useState<number>(-1);
    const optionsRefs = useRef<(HTMLButtonElement | null)[]>([]);

    const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
        if (showList === true)
            handleKeyDown(event, 'Tab', () => setShowList(false));
    };

    const handleTabKeyDownMemo = useMemo(() => handleTabKeyDown, [showList]);

    const handleRemove = (key: string) => {
        if (props.onRemove)
            props.onRemove(key);
    };

    const getText = () => {
        const optionText = props.options.find(o => o.key === props.value)?.text;
        return optionText ? optionText : props.placeHolderText;
    };

    const handleKeyDownButton = (event: KeyboardEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        handleKeyDown(event, "Enter", () => {
            setShowList(!showList);
            setFocusedIndex(0);
        });
        handleTabKeyDownMemo(event);
        if (event.key === "ArrowDown" && showList) {
            event.preventDefault();
            setFocusedIndex((prev) => {
                const next = prev < props.options.length - 1 ? prev + 1 : 0;
                return next;
            });
        }
        if (event.key === "ArrowUp" && showList) {
            event.preventDefault();
            setFocusedIndex((prev) => {
                const next = prev > 0 ? prev - 1 : props.options.length - 1;
                return next;
            });
        }
        if ((event.key === "Escape" || event.key === "Tab") && showList) {
            setShowList(false);
        }
        if ((event.key === "Enter" || event.key === " ") && showList && focusedIndex >= 0) {
            event.preventDefault();
            const opt = props.options[focusedIndex];
            if (opt && !opt.disabled) {
                props.onChange(opt.key);
                setShowList(false);
            }
        }
    };

    useEffect(() => {
        if (showList) {
            setFocusedIndex(0);
        } else {
            setFocusedIndex(-1);
        }
    }, [showList]);

    useEffect(() => {
        if (showList && focusedIndex >= 0 && optionsRefs.current[focusedIndex]) {
            optionsRefs.current[focusedIndex]?.focus();
        }
    }, [focusedIndex, showList]);

    return (
        <>
            <div ref={ref} className="ga-form-field ar-ga-dropdown">
                <button
                    type="button"
                    className="ga-select"
                    onClick={() => {
                        setShowList(!showList);
                        setFocusedIndex(0);
                    }}
                    onKeyDown={handleKeyDownButton}
                    title={getText()}
                >
                    <div className="ga-select__value">{getText()}</div>
                    {!showList && <ChevronDownIcon className="ga-select__arrow" size={16} />}
                    {showList && <ChevronUpIcon className="ga-select__arrow" size={16} />}
                </button>
                {showList &&
                    <div className="ga-dropdown">
                        {props.options.map((opt: IDropdownButtonItem, idx) =>
                            <button
                                key={opt.key}
                                ref={el => { optionsRefs.current[idx] = el; }}
                                className={`ga-dropdown__item ${(opt.disabled ? "ga-dropdown__item--disabled" : "")} ${opt.key === props.value ? "ga-dropdown__item--selected" : ""} ar-gap-06`}
                                onClick={(e: any) => {
                                    props.onChange(opt.key);
                                    setShowList(false);
                                }}
                                type="button"
                                disabled={opt.disabled}
                                tabIndex={focusedIndex === idx ? 0 : -1}
                                onKeyDown={handleKeyDownButton}
                            >
                                <div className="ga-dropdown__item-label">{opt.text}</div>
                                {opt.removable &&
                                    <div className="remove-opt" onClick={(event: React.MouseEvent<HTMLSpanElement>) => { event.stopPropagation(); handleRemove(opt.key); }}>
                                        <XIcon size={16} />
                                    </div>
                                }
                            </button>
                        )}
                    </div>
                }
            </div>
        </>
    );
}